/**
 * CHOOSE PATH (A04.5)
 *
 * Post-signup screen when user has no workspace.
 * Three options: create workspace, use invite code, or join detected workspace.
 * Design ref: A045ChoosePath in screens-states.jsx
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getBrandingConfig } from '@shared/config/brandingConfig';
import { useDynamicBranding } from '../hooks/useDynamicBranding';
import { supabase } from '@shared/lib/supabaseClient';
import AuthLayout from '../components/AuthLayout';

interface DetectedWorkspace {
  id: string;
  nombre: string;
  initials: string;
}

const ChoosePathPage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const app = params.get('app') || 'empresa';
  const returnTo = params.get('returnTo');

  useDynamicBranding(app);
  const branding = getBrandingConfig(app);

  const [detected, setDetected] = useState<DetectedWorkspace | null>(null);
  const [userEmail, setUserEmail] = useState('');

  // Check if user's email domain matches an existing workspace
  useEffect(() => {
    const checkDomain = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user?.email) return;
        setUserEmail(user.email);
        const domain = user.email.split('@')[1];
        if (!domain || domain.includes('gmail') || domain.includes('hotmail') || domain.includes('yahoo') || domain.includes('outlook')) return;

        const { data } = await supabase
          .from('empresas')
          .select('id, nombre')
          .ilike('subdomain', `%${domain.split('.')[0]}%`)
          .limit(1)
          .single();

        if (data) {
          const row = data as any;
          setDetected({
            id: row.id,
            nombre: row.nombre,
            initials: (row.nombre as string).split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase(),
          });
        }
      } catch (_) {
        // silent
      }
    };
    checkDomain();
  }, []);

  const buildQs = (path: string) => {
    const qs = new URLSearchParams();
    if (app) qs.set('app', app);
    if (returnTo) qs.set('returnTo', returnTo);
    return `${path}?${qs.toString()}`;
  };

  return (
    <AuthLayout>
      <div className="w-full flex flex-col" style={{ maxWidth: 520 }}>
        {/* Title */}
        <h1 className="text-xl font-semibold tracking-tight text-foreground mb-1">
          Que quieres hacer ahora?
        </h1>
        <p className="text-sm text-muted-foreground mb-6" style={{ maxWidth: '44ch', lineHeight: 1.55 }}>
          Tu cuenta personal esta lista. El siguiente paso es entrar a un workspace (la empresa). Elige el camino que aplique.
        </p>

        {/* Options */}
        <div className="flex flex-col gap-2.5">
          {/* Option 1: Create workspace */}
          <button
            type="button"
            onClick={() => navigate(buildQs('/create-workspace'))}
            className="group flex items-center gap-4 rounded-md border border-border bg-card p-4 text-left transition-all hover:border-foreground/20 hover:shadow-sm w-full"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent font-mono text-sm font-bold">
              +
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-foreground">Crear un workspace nuevo</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Soy dueno / admin de una empresa que va a usar ThePlatform360.
              </p>
            </div>
            <span className="text-muted-foreground text-base">→</span>
          </button>

          {/* Option 2: Have invite code */}
          <button
            type="button"
            onClick={() => navigate(buildQs('/join-workspace'))}
            className="group flex items-center gap-4 rounded-md border border-border bg-card p-4 text-left transition-all hover:border-foreground/20 hover:shadow-sm w-full"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground text-sm">
              @
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-foreground">Tengo un invite link o codigo</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Mi empresa ya tiene workspace y alguien me invito.
              </p>
            </div>
            <span className="text-muted-foreground text-base">→</span>
          </button>

          {/* Option 3: Domain-detected workspace (conditional) */}
          {detected && (
            <button
              type="button"
              onClick={() => navigate(buildQs(`/join-workspace?workspace_id=${detected.id}`))}
              className="group flex items-center gap-4 rounded-md border border-primary/30 bg-primary/5 p-4 text-left transition-all hover:border-primary/50 hover:shadow-sm w-full"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-foreground text-background font-mono text-xs font-bold">
                {detected.initials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-primary">
                  Unirme a {detected.nombre}{' '}
                  <span className="font-normal text-primary/70 text-[11px]">detectado por dominio</span>
                </p>
                <p className="text-xs text-primary/80 mt-0.5">
                  Tu email <code className="bg-transparent p-0 text-inherit">{userEmail ? `@${userEmail.split('@')[1]}` : ''}</code> coincide con este workspace existente.
                </p>
              </div>
              <span className="text-primary text-base">→</span>
            </button>
          )}

          {/* Ghost button: skip */}
          <button
            type="button"
            onClick={() => {
              if (returnTo) {
                window.location.href = returnTo;
              } else {
                navigate(buildQs('/login'));
              }
            }}
            className="mt-3 w-full text-center text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-2"
          >
            Configurar workspace mas tarde →
          </button>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-muted-foreground mt-6">
          Podras unirte o crear workspaces cuando quieras desde tu cuenta.
        </div>
      </div>
    </AuthLayout>
  );
};

export default ChoosePathPage;
